import { Link, useNavigate } from "react-router-dom";
import { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Inbox } from "lucide-react";

export const RecentBookingsCard = ({ stats, isClient }) => {
  const navigate = useNavigate();
  const bookings = stats?.recent_bookings || [];
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox className="h-4 w-4" /> Recent bookings
        </CardTitle>
        <Link to="/bookings" className="text-xs font-medium text-[hsl(174,38%,34%)] hover:underline">View all →</Link>
      </CardHeader>
      <CardContent>
        {bookings.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {isClient ? "No bookings yet — explore models to get started." : "No booking requests yet."}
          </p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <button key={b.id} data-testid="dashboard-recent-booking" onClick={() => navigate(`/bookings?id=${b.id}`)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border bg-card p-3 text-left transition-[background-color] hover:bg-muted">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{b.job?.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{b.model_name} · {b.client_company_name}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="font-mono-plex text-xs">{fmtMoney(b.agreed_amount ?? b.current_offer?.amount)}</span>
                  <StatusBadge status={b.status} />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const QuickActionsRail = ({ isClient }) => {
  const navigate = useNavigate();
  return (
    <div className="space-y-4 lg:col-span-4">
      <Card className="rounded-2xl">
        <CardContent className="space-y-3 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quick actions</p>
          {isClient ? (
            <>
              <Button data-testid="dashboard-explore-button" className="w-full rounded-xl" onClick={() => navigate("/explore")}>Explore models</Button>
              <Button variant="outline" className="w-full rounded-xl border bg-card hover:bg-muted" onClick={() => navigate("/wallet")}>Top up wallet</Button>
              <Button variant="outline" className="w-full rounded-xl border bg-card hover:bg-muted" onClick={() => navigate("/licenses")}>My licences</Button>
            </>
          ) : (
            <>
              <Button className="w-full rounded-xl" onClick={() => navigate("/bookings")}>Review requests</Button>
              <Button variant="outline" className="w-full rounded-xl border bg-card hover:bg-muted" onClick={() => navigate("/wallet")}>View earnings wallet</Button>
              <Button variant="outline" className="w-full rounded-xl border bg-card hover:bg-muted" onClick={() => navigate("/licenses")}>Active licences</Button>
            </>
          )}
        </CardContent>
      </Card>
      <Card className="rounded-2xl bg-[hsl(174,38%,96%)]">
        <CardContent className="space-y-2 p-5 text-sm">
          <p className="font-semibold">You're protected</p>
          <p className="text-muted-foreground">
            {isClient
              ? "Every job is AI-screened. Models must consent before you can use their look, and usage is strictly time-bound."
              : "No job reaches you without passing the AI ethical screen. You consent to every use of your look, and you're paid the full fee upfront."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
